import React, { useCallback, useEffect, useState } from 'react';
import {
  getDeviceToken,
  clearDeviceToken,
  STAFF_UNAUTHORIZED_EVENT,
} from '../lib/staffAuth';
import { staff } from '../lib/staffSession';

/**
 * Gates a staff screen. Accepts EITHER:
 *  - the new Staff Access OS session (reception/admin via login, master via
 *    station + PIN) — checked against /api/staff/me/; or
 *  - the legacy StaffDevice token (X-Device-Token) — kept for backward
 *    compatibility so nothing breaks during the migration.
 *
 * Role is enforced server-side; `role` here maps the gate to the new roles and
 * tailors the device-token prompt copy.
 */
const NEW_ROLE_OK = {
  RECEPTION: (r) => r === 'RECEPTION' || r === 'ADMIN',
  MASTER_TABLET: (r) => r === 'MASTER' || r === 'ADMIN' || r === 'RECEPTION',  // admin/reception may open tablets too
  STAFF: (r) => r === 'RECEPTION' || r === 'ADMIN' || r === 'MASTER',
};

export default function StaffDeviceGate({ role = 'STAFF', children }) {
  const [token, setToken] = useState(() => getDeviceToken());
  const [reauth, setReauth] = useState(false);
  // new-auth session: null = still checking, false = none, object = authenticated staff
  const [staffMe, setStaffMe] = useState(null);
  const [wrongRole, setWrongRole] = useState(null);   // signed in, but this role may not open the screen

  useEffect(() => {
    const onUnauthorized = () => {
      clearDeviceToken();
      setToken('');
      setReauth(true);
    };
    window.addEventListener(STAFF_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(STAFF_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  useEffect(() => {
    let alive = true;
    staff.me()
      .then((m) => {
        if (!alive) return;
        const ok = m && m.authenticated && (NEW_ROLE_OK[role] || NEW_ROLE_OK.STAFF)(m.role);
        setStaffMe(ok ? m : false);
        setWrongRole(m && m.authenticated && !ok ? m : null);
      })
      .catch(() => alive && setStaffMe(false));
    return () => { alive = false; };
  }, [role]);

  const signOut = useCallback(() => {
    clearDeviceToken();
    setToken('');
    setReauth(false);
  }, []);

  const newSignOut = useCallback(async () => {
    if (!window.confirm('Выйти из системы на этом устройстве?')) return;
    try {
      // Master: end the PIN session + personal workplace; reception/admin: end the login session.
      if (staffMe && staffMe.role === 'MASTER') { await staff.pinLogout(); await staff.stationClear().catch(() => {}); }
      else { await staff.logout(); }
    } catch (e) { /* fall through to the login page */ }
    window.location.href = '/staff';
  }, [staffMe]);

  const mustLogin = !token && (staffMe === false || reauth) && !wrongRole;
  useEffect(() => {
    if (!mustLogin) return;
    const here = window.location.pathname + window.location.search;
    window.location.replace('/staff?next=' + encodeURIComponent(here));
  }, [mustLogin]);

  // Legacy device token present -> render (device flow unchanged).
  if (token) {
    return (
      <>
        {children}
        <button type="button" onClick={signOut} style={corner}>Сменить устройство</button>
      </>
    );
  }

  // New Staff Access session present -> render.
  if (staffMe) {
    return (
      <>
        {children}
        <button type="button" onClick={newSignOut} style={corner}>
          {role === 'MASTER_TABLET' ? 'Сменить мастера' : 'Выйти'}
        </button>
      </>
    );
  }

  // Still checking the new-auth session -> brief loader (avoid flashing the prompt).
  if (staffMe === null) {
    return (
      <div style={center}><div style={{ color: '#8b8877', fontSize: 15 }}>Загрузка…</div></div>
    );
  }

  // Signed in with a role that cannot use this screen: say so (redirecting to the login would loop back here).
  if (wrongRole) {
    return (
      <div style={center}><div style={{ maxWidth: 380, textAlign: 'center', color: '#3f3029' }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Нет доступа</div>
        <div style={{ fontSize: 15, marginBottom: 16 }}>Этот раздел недоступен для вашей роли. Выйдите и войдите другим логином.</div>
        <button type="button" onClick={async () => { try { await staff.logout(); } catch (e) { /* ignore */ } window.location.href = '/staff'; }}
          style={{ padding: '10px 16px', borderRadius: 10, border: 0, background: '#273433', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Войти другим пользователем</button>
      </div></div>
    );
  }

  // Not signed in (or a role that can't use this screen): go to the normal staff login and come back here.
  // No device authorization screen any more (owner 2026-10-16).
  return <div style={center}><div style={{ color: '#8b8877', fontSize: 15 }}>Переход ко входу…</div></div>;
}

const center = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f7', padding: 16 };
const corner = { position: 'fixed', bottom: 8, right: 8, zIndex: 9999, fontSize: 12, padding: '4px 8px', opacity: 0.6, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' };
